import { useState } from 'react';
import {
  AlignLeft, AlignCenter, AlignRight, Bold, Italic,
  Copy, Trash2, ChevronUp, ChevronDown, RefreshCw, Image
} from 'lucide-react';
import type { ChartElement, ChartElementType, SectionType } from '@shared/types';

const SECTION_TYPES: { value: SectionType; label: string }[] = [
  { value: 'intro', label: 'Intro' },
  { value: 'verse', label: 'Verse' },
  { value: 'pre-chorus', label: 'Pre-Chorus' },
  { value: 'chorus', label: 'Chorus' },
  { value: 'bridge', label: 'Bridge' },
  { value: 'solo', label: 'Solo' },
  { value: 'outro', label: 'Outro' },
  { value: 'tag', label: 'Tag' },
  { value: 'breakdown', label: 'Breakdown' },
  { value: 'interlude', label: 'Interlude' },
  { value: 'vamp', label: 'Vamp' },
  { value: 'custom', label: 'Custom' },
];

const SECTION_STYLES = [
  { value: 'header-bar', label: 'Header Bar' },
  { value: 'underline', label: 'Underline' },
  { value: 'boxed', label: 'Boxed' },
  { value: 'plain', label: 'Plain' },
];

const FONT_SIZES = [10, 11, 12, 14, 16, 18, 20, 24, 28, 32];

interface InspectorPanelProps {
  element: ChartElement;
  onUpdate: (updates: Partial<ChartElement>) => void;
  onUpdateContent: (content: Record<string, any>) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onLayerUp: () => void;
  onLayerDown: () => void;
  onReplaceImage?: () => void;
  onEditElement?: () => void;
}

export function InspectorPanel({
  element, onUpdate, onUpdateContent, onDelete, onDuplicate,
  onLayerUp, onLayerDown, onReplaceImage, onEditElement
}: InspectorPanelProps) {
  return (
    <div className="w-56 bg-surface-900 border-l border-surface-700 overflow-y-auto shrink-0 flex flex-col">
      <div className="px-3 py-2 border-b border-surface-700 flex items-center justify-between">
        <span className="text-xs font-semibold text-gray-300 uppercase tracking-wider">
          {getTypeLabel(element.type)}
        </span>
        <div className="flex items-center gap-0.5">
          <button onClick={onDuplicate} className="p-1 hover:bg-surface-700 rounded" title="Duplicate">
            <Copy className="w-3.5 h-3.5 text-gray-400" />
          </button>
          <button onClick={onLayerUp} className="p-1 hover:bg-surface-700 rounded" title="Move up">
            <ChevronUp className="w-3.5 h-3.5 text-gray-400" />
          </button>
          <button onClick={onLayerDown} className="p-1 hover:bg-surface-700 rounded" title="Move down">
            <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
          </button>
          <button onClick={onDelete} className="p-1 hover:bg-red-900/40 rounded" title="Delete">
            <Trash2 className="w-3.5 h-3.5 text-red-400" />
          </button>
        </div>
      </div>

      <div className="p-3 space-y-3 text-xs">
        <PositionControls element={element} onUpdate={onUpdate} />

        {element.type === 'section' && (
          <SectionControls element={element} onUpdateContent={onUpdateContent} />
        )}
        {element.type === 'chord' && (
          <ChordControls element={element} onUpdateContent={onUpdateContent} />
        )}
        {element.type === 'title' && (
          <TitleControls element={element} onUpdateContent={onUpdateContent} />
        )}
        {element.type === 'tempo' && (
          <TempoControls element={element} onUpdateContent={onUpdateContent} />
        )}
        {element.type === 'key' && (
          <KeyControls element={element} onUpdateContent={onUpdateContent} />
        )}
        {(element.type === 'freetext' || element.type === 'lyrics' || element.type === 'notes') && (
          <TextControls element={element} onUpdateContent={onUpdateContent} />
        )}
        {element.type === 'image' && (
          <ImageControls element={element} onReplaceImage={onReplaceImage} />
        )}
        {element.type === 'divider' && (
          <DividerControls element={element} onUpdate={onUpdate} onUpdateContent={onUpdateContent} />
        )}
        {element.type === 'repeat' && (
          <RepeatControls element={element} onUpdateContent={onUpdateContent} />
        )}
        {element.type === 'link' && (
          <LinkControls element={element} onUpdateContent={onUpdateContent} />
        )}
        {element.type === 'songHeader' && (
          <SongHeaderControls element={element} onUpdateContent={onUpdateContent} />
        )}
        {element.type === 'songForm' && (
          <SongFormControls element={element} onUpdateContent={onUpdateContent} onEditElement={onEditElement} />
        )}
      </div>
    </div>
  );
}

function PositionControls({ element, onUpdate }: { element: ChartElement; onUpdate: (u: Partial<ChartElement>) => void }) {
  return (
    <div>
      <label className="block text-gray-500 mb-1 font-medium">Size</label>
      <div className="grid grid-cols-2 gap-1.5">
        <div>
          <span className="text-gray-600">W</span>
          <input
            type="number"
            value={Math.round(element.width)}
            onChange={e => onUpdate({ width: Math.max(30, Number(e.target.value)) })}
            className="w-full bg-surface-800 border border-surface-700 rounded px-1.5 py-1 text-xs text-gray-300 mt-0.5"
          />
        </div>
        <div>
          <span className="text-gray-600">H</span>
          <input
            type="number"
            value={Math.round(element.height)}
            onChange={e => onUpdate({ height: Math.max(20, Number(e.target.value)) })}
            className="w-full bg-surface-800 border border-surface-700 rounded px-1.5 py-1 text-xs text-gray-300 mt-0.5"
          />
        </div>
      </div>
    </div>
  );
}

function SectionControls({ element, onUpdateContent }: { element: ChartElement; onUpdateContent: (c: Record<string, any>) => void }) {
  return (
    <>
      <div>
        <label className="block text-gray-500 mb-1 font-medium">Section Type</label>
        <select
          value={element.content.type || 'verse'}
          onChange={e => {
            const t = e.target.value;
            const label = t === 'custom' ? (element.content.customLabel || element.content.label || 'Custom') : SECTION_TYPES.find(s => s.value === t)?.label || t;
            onUpdateContent({ type: t, label });
          }}
          className="w-full bg-surface-800 border border-surface-700 rounded px-1.5 py-1 text-xs text-gray-300"
        >
          {SECTION_TYPES.map(s => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-gray-500 mb-1 font-medium">Label</label>
        <input
          value={element.content.label || ''}
          onChange={e => onUpdateContent({ label: e.target.value, customLabel: e.target.value })}
          className="w-full bg-surface-800 border border-surface-700 rounded px-1.5 py-1 text-xs text-gray-300"
        />
      </div>
      <div>
        <label className="block text-gray-500 mb-1 font-medium">Style</label>
        <select
          value={element.content.style || 'header-bar'}
          onChange={e => onUpdateContent({ style: e.target.value })}
          className="w-full bg-surface-800 border border-surface-700 rounded px-1.5 py-1 text-xs text-gray-300"
        >
          {SECTION_STYLES.map(s => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
      </div>
      <FontSizeControl value={element.content.fontSize} onChange={v => onUpdateContent({ fontSize: v })} />
      <AlignmentControl value={element.content.alignment} onChange={v => onUpdateContent({ alignment: v })} />
    </>
  );
}

function ChordControls({ element, onUpdateContent }: { element: ChartElement; onUpdateContent: (c: Record<string, any>) => void }) {
  const mode = element.content.mode || 'standard';
  return (
    <>
      <div>
        <label className="block text-gray-500 mb-1 font-medium">
          {mode === 'standard' ? 'Chord' : 'Text'}
        </label>
        {mode === 'standard' ? (
          <textarea
            value={element.content.chord || ''}
            onChange={e => onUpdateContent({ chord: e.target.value })}
            className="w-full bg-surface-800 border border-surface-700 rounded px-1.5 py-1 text-xs text-gray-300 resize-none"
            rows={3}
            placeholder="Am7"
          />
        ) : (
          <textarea
            value={element.content.text || element.content.chord || ''}
            onChange={e => onUpdateContent({ text: e.target.value })}
            className="w-full bg-surface-800 border border-surface-700 rounded px-1.5 py-1 text-xs text-gray-300 resize-none"
            rows={3}
            placeholder="Am  Dm  G  C"
          />
        )}
      </div>
      <div>
        <label className="block text-gray-500 mb-1 font-medium">Mode</label>
        <div className="flex gap-1">
          <button
            onClick={() => onUpdateContent({ mode: 'standard' })}
            className={`flex-1 py-1 rounded text-center ${mode === 'standard' ? 'bg-primary-600 text-white' : 'bg-surface-800 text-gray-400 hover:bg-surface-700'}`}
          >
            Chord
          </button>
          <button
            onClick={() => onUpdateContent({ mode: 'freeform' })}
            className={`flex-1 py-1 rounded text-center ${mode === 'freeform' ? 'bg-primary-600 text-white' : 'bg-surface-800 text-gray-400 hover:bg-surface-700'}`}
          >
            Freeform
          </button>
        </div>
      </div>
      <div>
        <label className="block text-gray-500 mb-1 font-medium">Display</label>
        <div className="flex gap-1">
          <button
            onClick={() => onUpdateContent({ displayStyle: 'plain' })}
            className={`flex-1 py-1 rounded text-center ${(element.content.displayStyle || 'plain') === 'plain' ? 'bg-primary-600 text-white' : 'bg-surface-800 text-gray-400 hover:bg-surface-700'}`}
          >
            Plain
          </button>
          <button
            onClick={() => onUpdateContent({ displayStyle: 'chord-box' })}
            className={`flex-1 py-1 rounded text-center ${element.content.displayStyle === 'chord-box' ? 'bg-primary-600 text-white' : 'bg-surface-800 text-gray-400 hover:bg-surface-700'}`}
          >
            Boxed
          </button>
        </div>
      </div>
      <FontSizeControl value={element.content.fontSize} onChange={v => onUpdateContent({ fontSize: v })} />
    </>
  );
}

function TitleControls({ element, onUpdateContent }: { element: ChartElement; onUpdateContent: (c: Record<string, any>) => void }) {
  return (
    <>
      <div>
        <label className="block text-gray-500 mb-1 font-medium">Title Text</label>
        <input
          value={element.content.text || ''}
          onChange={e => onUpdateContent({ text: e.target.value })}
          className="w-full bg-surface-800 border border-surface-700 rounded px-1.5 py-1 text-xs text-gray-300"
        />
      </div>
      <FontSizeControl value={element.content.fontSize} onChange={v => onUpdateContent({ fontSize: v })} />
    </>
  );
}

function TempoControls({ element, onUpdateContent }: { element: ChartElement; onUpdateContent: (c: Record<string, any>) => void }) {
  return (
    <div>
      <label className="block text-gray-500 mb-1 font-medium">BPM</label>
      <input
        type="number"
        value={element.content.bpm || ''}
        onChange={e => onUpdateContent({ bpm: Number(e.target.value) })}
        className="w-full bg-surface-800 border border-surface-700 rounded px-1.5 py-1 text-xs text-gray-300"
      />
    </div>
  );
}

function KeyControls({ element, onUpdateContent }: { element: ChartElement; onUpdateContent: (c: Record<string, any>) => void }) {
  return (
    <div>
      <label className="block text-gray-500 mb-1 font-medium">Key</label>
      <input
        value={element.content.key || ''}
        onChange={e => onUpdateContent({ key: e.target.value })}
        className="w-full bg-surface-800 border border-surface-700 rounded px-1.5 py-1 text-xs text-gray-300"
        placeholder="C, Am, Bb..."
      />
    </div>
  );
}

function TextControls({ element, onUpdateContent }: { element: ChartElement; onUpdateContent: (c: Record<string, any>) => void }) {
  return (
    <>
      <div>
        <label className="block text-gray-500 mb-1 font-medium">Text</label>
        <textarea
          value={element.content.text || ''}
          onChange={e => onUpdateContent({ text: e.target.value })}
          className="w-full bg-surface-800 border border-surface-700 rounded px-1.5 py-1 text-xs text-gray-300 resize-none"
          rows={4}
        />
      </div>
      <FontSizeControl value={element.content.fontSize} onChange={v => onUpdateContent({ fontSize: v })} />
      <div>
        <label className="block text-gray-500 mb-1 font-medium">Emphasis</label>
        <div className="flex gap-1">
          <button
            onClick={() => onUpdateContent({ fontWeight: element.content.fontWeight === 'bold' ? 'normal' : 'bold' })}
            className={`p-1.5 rounded ${element.content.fontWeight === 'bold' ? 'bg-primary-600 text-white' : 'bg-surface-800 text-gray-400 hover:bg-surface-700'}`}
          >
            <Bold className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => onUpdateContent({ fontStyle: element.content.fontStyle === 'italic' ? 'normal' : 'italic' })}
            className={`p-1.5 rounded ${element.content.fontStyle === 'italic' ? 'bg-primary-600 text-white' : 'bg-surface-800 text-gray-400 hover:bg-surface-700'}`}
          >
            <Italic className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </>
  );
}

function ImageControls({ element, onReplaceImage }: { element: ChartElement; onReplaceImage?: () => void }) {
  return (
    <>
      <div className="text-gray-500">
        {element.content.filename && (
          <p className="truncate mb-1">{element.content.filename}</p>
        )}
      </div>
      {onReplaceImage && (
        <button
          onClick={onReplaceImage}
          className="flex items-center gap-1.5 w-full bg-surface-800 hover:bg-surface-700 text-gray-300 py-1.5 px-2 rounded text-xs"
        >
          <RefreshCw className="w-3 h-3" />
          Replace Image
        </button>
      )}
    </>
  );
}

function DividerControls({ element, onUpdate, onUpdateContent }: {
  element: ChartElement;
  onUpdate: (u: Partial<ChartElement>) => void;
  onUpdateContent: (c: Record<string, any>) => void;
}) {
  const orientation: 'horizontal' | 'vertical' = element.content.orientation || 'horizontal';

  const switchOrientation = (newOrientation: 'horizontal' | 'vertical') => {
    if (newOrientation === orientation) return;
    const span = Math.max(element.width, element.height);
    const newWidth  = newOrientation === 'horizontal' ? span : 12;
    const newHeight = newOrientation === 'horizontal' ? 12   : span;
    onUpdateContent({ orientation: newOrientation });
    onUpdate({ width: newWidth, height: newHeight });
  };

  return (
    <div className="space-y-2">
      <div>
        <label className="block text-gray-500 mb-1 font-medium">Orientation</label>
        <div className="flex gap-1">
          {(['horizontal', 'vertical'] as const).map(o => (
            <button
              key={o}
              onClick={() => switchOrientation(o)}
              className={`flex-1 py-1 rounded text-xs font-medium transition-colors capitalize ${
                orientation === o
                  ? 'bg-primary-600 text-white'
                  : 'bg-surface-800 text-gray-400 hover:bg-surface-700 hover:text-gray-200'
              }`}
            >
              {o === 'horizontal' ? '─ Horiz' : '│ Vert'}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="block text-gray-500 mb-1 font-medium">Label (optional)</label>
        <input
          value={element.content.label || ''}
          onChange={e => onUpdateContent({ label: e.target.value })}
          className="w-full bg-surface-800 border border-surface-700 rounded px-1.5 py-1 text-xs text-gray-300"
          placeholder="e.g. Break"
        />
      </div>
    </div>
  );
}

function RepeatControls({ element, onUpdateContent }: { element: ChartElement; onUpdateContent: (c: Record<string, any>) => void }) {
  return (
    <div>
      <label className="block text-gray-500 mb-1 font-medium">Repeat Count</label>
      <input
        type="number"
        min={1}
        value={element.content.count || 2}
        onChange={e => onUpdateContent({ count: Math.max(1, Number(e.target.value)) })}
        className="w-full bg-surface-800 border border-surface-700 rounded px-1.5 py-1 text-xs text-gray-300"
      />
    </div>
  );
}

function FontSizeControl({ value, onChange }: { value?: number; onChange: (v: number) => void }) {
  return (
    <div>
      <label className="block text-gray-500 mb-1 font-medium">Font Size</label>
      <select
        value={value || 14}
        onChange={e => onChange(Number(e.target.value))}
        className="w-full bg-surface-800 border border-surface-700 rounded px-1.5 py-1 text-xs text-gray-300"
      >
        {FONT_SIZES.map(s => (
          <option key={s} value={s}>{s}px</option>
        ))}
      </select>
    </div>
  );
}

function AlignmentControl({ value, onChange }: { value?: string; onChange: (v: string) => void }) {
  const align = value || 'left';
  return (
    <div>
      <label className="block text-gray-500 mb-1 font-medium">Alignment</label>
      <div className="flex gap-1">
        {[
          { val: 'left', Icon: AlignLeft },
          { val: 'center', Icon: AlignCenter },
          { val: 'right', Icon: AlignRight },
        ].map(({ val, Icon }) => (
          <button
            key={val}
            onClick={() => onChange(val)}
            className={`p-1.5 rounded ${align === val ? 'bg-primary-600 text-white' : 'bg-surface-800 text-gray-400 hover:bg-surface-700'}`}
          >
            <Icon className="w-3.5 h-3.5" />
          </button>
        ))}
      </div>
    </div>
  );
}

function LinkControls({ element, onUpdateContent }: { element: ChartElement; onUpdateContent: (c: Record<string, any>) => void }) {
  return (
    <>
      <div>
        <label className="block text-gray-500 mb-1 font-medium">Label</label>
        <input
          value={element.content.label || ''}
          onChange={e => onUpdateContent({ label: e.target.value })}
          className="w-full bg-surface-800 border border-surface-700 rounded px-1.5 py-1 text-xs text-gray-300"
          placeholder="Reference Recording"
        />
      </div>
      <div>
        <label className="block text-gray-500 mb-1 font-medium">URL</label>
        <input
          value={element.content.url || ''}
          onChange={e => onUpdateContent({ url: e.target.value })}
          className="w-full bg-surface-800 border border-surface-700 rounded px-1.5 py-1 text-xs text-gray-300"
          placeholder="https://..."
        />
      </div>
      {element.content.url && (
        <button
          onClick={() => window.open(element.content.url, '_blank', 'noopener,noreferrer')}
          className="w-full py-1.5 rounded text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors"
        >
          Open Link
        </button>
      )}
    </>
  );
}

function SongHeaderControls({ element, onUpdateContent }: { element: ChartElement; onUpdateContent: (c: Record<string, any>) => void }) {
  return (
    <>
      {element.content.protected && (
        <div className="text-[10px] text-amber-400 bg-amber-900/20 border border-amber-800/40 rounded px-2 py-1">
          Linked to song details
        </div>
      )}
      <div>
        <label className="block text-gray-500 mb-1 font-medium">Song Title</label>
        <input
          value={element.content.title || ''}
          onChange={e => onUpdateContent({ title: e.target.value })}
          className="w-full bg-surface-800 border border-surface-700 rounded px-1.5 py-1 text-xs text-gray-300"
          placeholder="Song Title"
        />
      </div>
      <div>
        <label className="block text-gray-500 mb-1 font-medium">Artist</label>
        <input
          value={element.content.artist || ''}
          onChange={e => onUpdateContent({ artist: e.target.value })}
          className="w-full bg-surface-800 border border-surface-700 rounded px-1.5 py-1 text-xs text-gray-300"
          placeholder="Artist name"
        />
      </div>
      <div>
        <label className="block text-gray-500 mb-1 font-medium">Key</label>
        <input
          value={element.content.key || ''}
          onChange={e => onUpdateContent({ key: e.target.value })}
          className="w-full bg-surface-800 border border-surface-700 rounded px-1.5 py-1 text-xs text-gray-300"
          placeholder="C, Am, Bb…"
        />
      </div>
      <div>
        <label className="block text-gray-500 mb-1 font-medium">BPM</label>
        <input
          type="number"
          value={element.content.bpm ?? ''}
          onChange={e => onUpdateContent({ bpm: e.target.value === '' ? null : Number(e.target.value) })}
          className="w-full bg-surface-800 border border-surface-700 rounded px-1.5 py-1 text-xs text-gray-300"
          placeholder="120"
        />
      </div>
    </>
  );
}

function SongFormControls({
  element, onUpdateContent, onEditElement
}: {
  element: ChartElement;
  onUpdateContent: (c: Record<string, any>) => void;
  onEditElement?: () => void;
}) {
  const mode = (element.content.mode as 'names' | 'abc') || 'names';
  return (
    <div className="space-y-2">
      <div>
        <label className="block text-gray-500 mb-1 font-medium">Mode</label>
        <div className="flex gap-1">
          {(['names', 'abc'] as const).map(m => (
            <button
              key={m}
              onClick={() => onUpdateContent({ mode: m })}
              className={`flex-1 py-1 rounded text-center text-xs ${
                mode === m ? 'bg-primary-600 text-white' : 'bg-surface-800 text-gray-400 hover:bg-surface-700'
              }`}
            >
              {m === 'names' ? 'Names' : 'A/B/C'}
            </button>
          ))}
        </div>
      </div>
      {onEditElement && (
        <button
          onClick={onEditElement}
          className="w-full py-1 rounded text-xs bg-surface-800 text-gray-300 hover:bg-surface-700 border border-surface-600"
        >
          Edit Form
        </button>
      )}
    </div>
  );
}

function getTypeLabel(type: ChartElementType): string {
  const labels: Record<ChartElementType, string> = {
    title: 'Title',
    tempo: 'Tempo',
    key: 'Key',
    section: 'Section',
    chord: 'Chord',
    lyrics: 'Lyrics',
    notes: 'Notes',
    freetext: 'Text',
    image: 'Image',
    divider: 'Divider',
    repeat: 'Repeat',
    link: 'Link',
    songHeader: 'Song Header',
    songForm: 'Song Form',
  };
  return labels[type] || type;
}
